import AccessTimeIcon from "@mui/icons-material/AccessTime";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import Box from "@mui/material/Box";
import FormControlLabel from "@mui/material/FormControlLabel";
import IconButton from "@mui/material/IconButton";
import Popover from "@mui/material/Popover";
import Radio from "@mui/material/Radio";
import RadioGroup from "@mui/material/RadioGroup";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import {alpha} from "@mui/material/styles";
import {daySlices} from "crqlar-react-common/dist/utils/hooks/reservations/calendar/calendar.util";
import {Fragment, useState} from "react";
import {useTranslation} from "react-i18next";
import {RightSidebarFloatingPanel} from "../../../layout/rightSidebar/RightSidebarFloatingPanel";
import {RightSidebarPanelHeader} from "../../../layout/rightSidebar/RightSidebarPanelHeader";
import {RightSidebarPanelButton} from "../../../layout/rightSidebar/RightSidebarRail";
import {
    FLOORPLAN_DAY_SLICES,
    type FloorplanDaySlice,
} from "../../floorPlan/floorplanState";
import {useTimeframe} from "../../floorPlan/v2/hooks/useTimeframe";

type FixedDaySlice = Exclude<FloorplanDaySlice, "now" | "custom">;

const fixedTimeRange = (slice: FixedDaySlice) => {
    const [start, end] = daySlices(slice, "day");
    return {start, end};
};

export function FloorplanDayPicker() {
    const {t} = useTranslation();
    const {
        reservationDaySlice,
        customTimeframe,
        setReservationDaySlice,
    } = useTimeframe();
    const [infoAnchorEl, setInfoAnchorEl] = useState<HTMLElement | null>(null);

    const updateCustomStart = (start: string) => {
        setReservationDaySlice("custom", {
            start,
            end: start > customTimeframe.end ? "23:59" : customTimeframe.end,
        });
    };

    const updateCustomEnd = (end: string) => {
        setReservationDaySlice("custom", {
            start: end < customTimeframe.start ? "00:00" : customTimeframe.start,
            end,
        });
    };

    return (
        <RightSidebarPanelButton
            icon={<AccessTimeIcon/>}
            label={t(`Day part restaurant label ${reservationDaySlice}`)}
            renderPanel={close => (
                <RightSidebarFloatingPanel sx={{overflow: "hidden"}}>
                    <RightSidebarPanelHeader
                        title={t("Part of the day")}
                        titleAdornment={(
                            <IconButton
                                type="button"
                                size="small"
                                aria-label={t("Time filter info")}
                                aria-expanded={Boolean(infoAnchorEl)}
                                onClick={event => setInfoAnchorEl(event.currentTarget)}
                                sx={{p: 0.25, color: "text.secondary"}}
                            >
                                <InfoOutlinedIcon fontSize="small"/>
                            </IconButton>
                        )}
                        onClose={close}
                    />

                    <Stack sx={{flex: 1, minHeight: 0, overflowY: "auto", px: 2, pb: 2}}>
                        <RadioGroup
                            value={reservationDaySlice}
                            onChange={event => setReservationDaySlice(
                                event.target.value as FloorplanDaySlice,
                            )}
                        >
                            {FLOORPLAN_DAY_SLICES.map(slice => {
                                const selected = reservationDaySlice === slice;
                                const range = slice === "morning" || slice === "day" || slice === "evening"
                                    ? fixedTimeRange(slice)
                                    : undefined;

                                return (
                                    <Fragment key={slice}>
                                        <FormControlLabel
                                            value={slice}
                                            control={<Radio size="small" sx={{p: 0.75, mr: 0.5}}/>}
                                            label={(
                                                <Typography variant="body1">
                                                    {t(`Day part restaurant label ${slice}`)}
                                                    {range ? ` (${range.start} - ${range.end})` : ""}
                                                </Typography>
                                            )}
                                            sx={theme => ({
                                                m: 0,
                                                px: 0.75,
                                                py: 0.25,
                                                width: "100%",
                                                minWidth: 0,
                                                borderRadius: 1,
                                                bgcolor: selected
                                                    ? alpha(theme.palette.primary.main, 0.06)
                                                    : "transparent",
                                                "&:hover": {
                                                    bgcolor: selected
                                                        ? alpha(theme.palette.primary.main, 0.12)
                                                        : theme.palette.action.hover,
                                                },
                                            })}
                                        />

                                        {slice === "custom" && selected && (
                                            <Stack direction="row" gap={1.5} sx={{pl: 4, pr: 1, pt: 2, pb: 1}}>
                                                <TextField
                                                    fullWidth
                                                    type="time"
                                                    size="small"
                                                    label={t("From")}
                                                    value={customTimeframe.start}
                                                    onChange={event => updateCustomStart(event.target.value)}
                                                    InputLabelProps={{shrink: true}}
                                                    inputProps={{max: customTimeframe.end}}
                                                />
                                                <TextField
                                                    fullWidth
                                                    type="time"
                                                    size="small"
                                                    label={t("To")}
                                                    value={customTimeframe.end}
                                                    onChange={event => updateCustomEnd(event.target.value)}
                                                    InputLabelProps={{shrink: true}}
                                                    inputProps={{min: customTimeframe.start}}
                                                />
                                            </Stack>
                                        )}
                                    </Fragment>
                                );
                            })}
                        </RadioGroup>
                    </Stack>

                    <Popover
                        open={Boolean(infoAnchorEl)}
                        anchorEl={infoAnchorEl}
                        anchorOrigin={{vertical: "bottom", horizontal: "left"}}
                        transformOrigin={{vertical: "top", horizontal: "left"}}
                        onClose={() => setInfoAnchorEl(null)}
                    >
                        <Box sx={{p: 2, width: 300, maxWidth: "calc(100vw - 32px)"}}>
                            <Typography variant="subtitle2" sx={{mb: 0.75}}>
                                {t("Time filter info")}
                            </Typography>
                            <Typography variant="body2" sx={{mb: 0.5, fontWeight: 600}}>
                                {t("Now")}:
                            </Typography>
                            <Typography variant="body2" color="text.secondary" sx={{mb: 1.5}}>
                                {t("Shows the current or next upcoming reservation for each table. If a table has no active reservation, it displays the next scheduled one regardless of time.")}
                            </Typography>
                            <Typography variant="body2" sx={{mb: 0.5, fontWeight: 600}}>
                                {t("Time ranges")}:
                            </Typography>
                            <Stack gap={0.5}>
                                {(["morning", "day", "evening"] as const).map(slice => {
                                    const range = fixedTimeRange(slice);
                                    return (
                                        <Typography key={slice} variant="body2">
                                            <Box component="span" sx={{fontWeight: 600}}>
                                                {t(`Day part restaurant label ${slice}`)}:
                                            </Box>{" "}
                                            {range.start} - {range.end}
                                        </Typography>
                                    );
                                })}
                            </Stack>
                        </Box>
                    </Popover>
                </RightSidebarFloatingPanel>
            )}
        />
    );
}
